import { Button } from '@mantine/core';
import { IconArrowRight, IconCheck, IconLock, IconSettings } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import type { categories } from '@shared/config/categories';

import classes from './category-game-card.module.css';

type Props = {
  category: (typeof categories)[number];
  enabled: boolean;
  to: '/play/campaigns/$categoryId' | '/play/daily/$categoryId' | '/play/endless/$categoryId';
  search?: { date?: string };
  onAction?: () => void;
  actionIcon?: ReactNode;
  children?: ReactNode;
  compact?: boolean;
  dense?: boolean;
  hideAction?: boolean;
  largeArtwork?: boolean;
  completed?: boolean;
  actionLabel: string;
  disabledLabel?: string;
  onDisabledClick?: () => void;
};

export const CategoryGameCard = ({
  category,
  enabled,
  to,
  search,
  onAction,
  actionIcon,
  children,
  actionLabel,
  disabledLabel = 'Не выбрана',
  onDisabledClick,
  compact,
  dense,
  hideAction,
  largeArtwork,
  completed,
}: Props) => {
  const content = (
    <>
      <span className={classes.categoryArtBubble} aria-hidden="true">
        <img className={classes.categoryArt} src={category.image} alt="" />
      </span>
      <span className={classes.categoryCopy}>
        <strong>{category.label}</strong>
        {children}
        {!hideAction && (
          <Button
            component="span"
            variant={compact && enabled ? 'game' : 'unstyled'}
            className={classes.categoryProgress}
            data-complete={completed || undefined}
            rightSection={
              actionIcon ??
              (enabled ? (
                completed ? (
                  <IconCheck size={20} aria-hidden="true" />
                ) : (
                  <IconArrowRight size={20} aria-hidden="true" />
                )
              ) : onDisabledClick ? (
                <IconSettings size={19} aria-hidden="true" />
              ) : (
                <IconLock size={19} aria-hidden="true" />
              ))
            }
          >
            {enabled ? actionLabel : disabledLabel}
          </Button>
        )}
      </span>
    </>
  );
  return enabled && !onAction ? (
    <Link
      to={to}
      params={{ categoryId: category.id }}
      search={search}
      className={classes.categoryCard}
      data-category={category.id}
      data-compact={compact || undefined}
      data-dense={dense || undefined}
      data-hide-action={hideAction || undefined}
      data-large-artwork={largeArtwork || undefined}
      aria-label={hideAction ? `${actionLabel} «${category.label}»` : undefined}
    >
      {content}
    </Link>
  ) : (
    <button
      type="button"
      className={classes.categoryCard}
      data-category={category.id}
      data-disabled={!enabled || undefined}
      data-complete={completed || undefined}
      data-compact={compact || undefined}
      data-dense={dense || undefined}
      data-hide-action={hideAction || undefined}
      data-large-artwork={largeArtwork || undefined}
      disabled={!enabled && !onDisabledClick}
      onClick={enabled ? onAction : onDisabledClick}
      aria-label={
        enabled
          ? `${actionLabel} «${category.label}»`
          : onDisabledClick
            ? `Выбрать категорию «${category.label}» в настройках`
            : `${category.label}: ${disabledLabel}`
      }
      aria-haspopup={onAction || onDisabledClick ? 'dialog' : undefined}
    >
      {content}
    </button>
  );
};
